import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ArrowLeft, CheckCircle2, XCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [tokenState, setTokenState] = useState({ checking: true, ok: false, email: "", error: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenState({ checking: false, ok: false, email: "", error: "No token in URL." });
      return;
    }
    api.get(`/auth/reset-password/${token}`)
      .then((r) => setTokenState({ checking: false, ok: true, email: r.data.email, error: "" }))
      .catch((e) => setTokenState({ checking: false, ok: false, email: "", error: e.response?.data?.detail || "Invalid link." }));
  }, [token]);

  const strong = pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw);
  const match = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) return toast.error("Password must be 8+ chars with letters and digits");
    if (!match) return toast.error("Passwords do not match");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md space-y-6" data-testid="reset-password-card">
        <div className="text-center">
          <div className="font-display text-2xl tracking-widest font-semibold">THE&nbsp;WOODLAND</div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">ERP · Set a new password</div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-sm">
          {tokenState.checking && <div className="text-center py-6 text-sm text-muted-foreground">Validating link…</div>}
          {!tokenState.checking && !tokenState.ok && (
            <div className="text-center py-4">
              <XCircle className="w-10 h-10 mx-auto text-rose-500 mb-3" />
              <h2 className="text-base font-semibold mb-1">Reset link problem</h2>
              <p className="text-sm text-muted-foreground">{tokenState.error}</p>
              <Link to="/forgot-password" className="inline-block mt-4 text-sm text-primary hover:underline" data-testid="reset-request-new">Request a new link</Link>
            </div>
          )}
          {!tokenState.checking && tokenState.ok && !done && (
            <>
              <h1 className="text-lg font-semibold mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-primary" /> Set your new password</h1>
              <p className="text-xs text-muted-foreground mb-4">Resetting for <span className="font-medium text-foreground">{tokenState.email}</span></p>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label>New password</Label>
                  <div className="relative">
                    <Input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 chars, letters + digits" required data-testid="reset-new-pw" />
                    <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground">
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className={`text-[10px] mt-1 ${strong ? "text-emerald-500" : "text-muted-foreground"}`}>
                    {strong ? "✓ Strong enough" : "8+ characters, must include letters & digits"}
                  </div>
                </div>
                <div>
                  <Label>Confirm new password</Label>
                  <Input type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-confirm-pw" />
                  {pw2 && !match && <div className="text-[10px] mt-1 text-rose-500">Passwords do not match</div>}
                </div>
                <Button type="submit" disabled={submitting || !strong || !match} className="w-full bg-primary text-primary-foreground" data-testid="reset-submit">
                  {submitting ? "Updating…" : "Set new password"}
                </Button>
              </form>
              <p className="text-[10px] text-muted-foreground mt-3">Resetting your password will sign you out from all devices.</p>
            </>
          )}
          {done && (
            <div className="text-center py-6">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-3" />
              <h2 className="text-base font-semibold mb-1">Password updated</h2>
              <p className="text-sm text-muted-foreground">You&rsquo;ll be redirected to sign in.</p>
            </div>
          )}
        </div>
        <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground flex items-center justify-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
